import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus, Users, FlaskConical, Pill, ListChecks, BarChart3, KeyRound, Pencil, Scissors, DatabaseBackup, ShieldCheck, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { BRANDS, branchesFor, brandName, GROOMING_CATALOG } from "@/lib/brands";
import { backupDownloadUrl } from "@/lib/api";
import { Navigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

function CrudList({ title, endpoint, testid }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");

  const load = async () => setItems((await api.get(endpoint)).data);
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try { await api.post(endpoint, { name: name.trim() }); setName(""); load(); toast.success("Added"); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const del = async (id) => { await api.delete(`${endpoint}/${id}`); load(); };

  return (
    <Card className="p-5 border-border/60">
      <h3 className="font-primary font-semibold mb-3">{title}</h3>
      <form onSubmit={add} className="flex gap-2 mb-3">
        <Input data-testid={`${testid}-name`} placeholder={`New ${title.toLowerCase()}…`} value={name} onChange={(e) => setName(e.target.value)} />
        <Button data-testid={`${testid}-add-btn`} type="submit"><Plus className="h-4 w-4" /></Button>
      </form>
      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-border/60">
            <span className="text-sm">{it.name}</span>
            <Button size="icon" variant="ghost" onClick={() => del(it.id)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PasswordDialog({ userId, userName }) {
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/users/${userId}/change-password`, { new_password: pwd }); toast.success("Password updated"); setOpen(false); setPwd(""); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="icon" variant="ghost" data-testid={`pwd-${userId}`}><KeyRound className="h-4 w-4" /></Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Change password for {userName}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input type="password" placeholder="New password (min 6)" value={pwd} onChange={(e) => setPwd(e.target.value)} required minLength={6} />
          <Button type="submit" className="w-full">Update password</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AnalyticsPanel() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)).catch(() => {}); }, []);
  if (!a) return <div className="text-muted-foreground p-4">Loading…</div>;
  const Stat = ({ label, value }) => (
    <Card className="p-5 border-border/60">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-primary text-3xl font-bold mt-2">{value}</div>
    </Card>
  );
  const List = ({ title, items }) => (
    <Card className="p-5 border-border/60">
      <h3 className="font-primary font-semibold mb-3">{title}</h3>
      {items.length === 0 && <div className="text-sm text-muted-foreground">No data yet.</div>}
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={it.name || it.month || i} className="flex items-center justify-between text-sm">
            <span className="truncate">{it.name || it.month}</span>
            <span className="font-mono text-muted-foreground">{it.count}</span>
          </div>
        ))}
      </div>
    </Card>
  );
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Patients" value={a.totals.patients} />
        <Stat label="Consultations" value={a.totals.consultations} />
        <Stat label="Users" value={a.totals.users} />
        <Stat label="Appointments" value={a.totals.appointments} />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <List title="Top Diagnoses" items={a.top_diagnoses} />
        <List title="Top Doctors" items={a.top_doctors} />
        <List title="Species Distribution" items={a.species} />
        <List title="Consultations by Month" items={a.by_month} />
      </div>
    </div>
  );
}

function StaffPanel() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "doctor", brand: "", branch: "", registration_number: "", phone: "" });
  const load = async () => setUsers((await api.get("/users")).data);
  useEffect(() => { load(); }, []);
  const needsBrand = form.role === "doctor" || form.role === "staff";
  const add = async (e) => {
    e.preventDefault();
    if (needsBrand && (!form.brand || !form.branch)) { toast.error("Select brand and branch for doctor/staff"); return; }
    try {
      const payload = { ...form };
      if (!needsBrand) { payload.brand = null; payload.branch = null; }
      await api.post("/auth/register", payload);
      toast.success("User created");
      setForm({ name: "", email: "", password: "", role: "doctor", brand: "", branch: "", registration_number: "", phone: "" }); load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/users/${id}`); load(); toast.success("User deleted"); };

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Card className="p-5 border-border/60">
        <h3 className="font-primary font-semibold mb-3">Add User</h3>
        <form onSubmit={add} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required /></div>
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({...form, role: v, brand: "", branch: ""})}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="doctor">Doctor</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          {needsBrand && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Brand</Label>
                <Select value={form.brand} onValueChange={(v) => setForm({...form, brand: v, branch: ""})}>
                  <SelectTrigger data-testid="user-brand"><SelectValue placeholder="Brand" /></SelectTrigger>
                  <SelectContent>{BRANDS.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Branch</Label>
                <Select value={form.branch} onValueChange={(v) => setForm({...form, branch: v})} disabled={!form.brand}>
                  <SelectTrigger data-testid="user-branch"><SelectValue placeholder="Branch" /></SelectTrigger>
                  <SelectContent>{branchesFor(form.brand).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
          )}
          <div className="space-y-1.5"><Label>Email</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required /></div>
          <div className="space-y-1.5"><Label>Password</Label><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Registration No.</Label><Input value={form.registration_number} onChange={(e) => setForm({...form, registration_number: e.target.value})} /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
          </div>
          <Button type="submit" className="w-full" data-testid="create-user-btn">Create User</Button>
        </form>
      </Card>
      <Card className="p-5 border-border/60">
        <h3 className="font-primary font-semibold mb-3">All Users</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {users.map((u) => (
            <div key={u.id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-border/60">
              <div>
                <div className="text-sm font-medium">{u.name} <span className="text-xs text-muted-foreground capitalize">· {u.role}</span></div>
                <div className="text-xs text-muted-foreground">{u.email}{u.brand ? ` · ${brandName(u.brand)}${u.branch ? " / " + u.branch : ""}` : u.role === "admin" ? " · All brands" : ""}</div>
              </div>
              <div className="flex items-center gap-1">
                <PasswordDialog userId={u.id} userName={u.name} />
                <Button size="icon" variant="ghost" onClick={() => del(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function PricingPanel() {
  const [brand, setBrand] = useState("barkode");
  const [prices, setPrices] = useState({});
  const [bands, setBands] = useState([]);
  const load = async (b) => {
    const { data } = await api.get(`/grooming-prices`, { params: { brand: b } });
    setPrices(data.prices || {}); setBands(data.age_bands || []);
  };
  useEffect(() => { load(brand); }, [brand]);
  const setVal = (label, band, v) => setPrices((p) => ({ ...p, [label]: { ...(p[label] || {}), [band]: v === "" ? 0 : parseFloat(v) } }));
  const save = async () => {
    try { await api.put("/grooming-prices", { brand, prices }); toast.success("Pricing saved"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const labels = GROOMING_CATALOG.flatMap((c) =>
    c.type === "single" ? [c.label] : (c.options || []).map((o) => `${c.label}: ${o}`)
  );
  return (
    <Card className="p-5 border-border/60">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="font-primary font-semibold">Age-based Grooming Pricing</h3>
          <p className="text-xs text-muted-foreground">Prices auto-fill at checkout based on each pet's current age band.</p>
        </div>
        <div className="flex gap-2">
          <Select value={brand} onValueChange={setBrand}>
            <SelectTrigger className="w-44" data-testid="pricing-brand"><SelectValue /></SelectTrigger>
            <SelectContent>{BRANDS.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={save} data-testid="save-pricing-btn">Save</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="py-2 pr-4">Service</th>
              {bands.map((b) => <th key={b.key} className="py-2 px-2 text-center">{b.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {labels.map((label) => (
              <tr key={label} className="border-b border-border/50">
                <td className="py-1.5 pr-4">{label}</td>
                {bands.map((b) => (
                  <td key={b.key} className="py-1.5 px-2">
                    <Input type="number" min="0" className="h-8 w-24 mx-auto text-center"
                           data-testid={`price-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${b.key}`}
                           value={prices[label]?.[b.key] ?? ""} onChange={(e) => setVal(label, b.key, e.target.value)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


function BackupPanel() {
  const [downloading, setDownloading] = useState(false);
  const download = () => {
    setDownloading(true);
    // Native navigation streams the ZIP straight to disk (works for very large exports).
    window.location.href = backupDownloadUrl();
    toast.success("Preparing your backup — the download will start shortly.");
    setTimeout(() => setDownloading(false), 4000);
  };
  return (
    <Card className="p-6 border-border/60 max-w-2xl" data-testid="backup-panel">
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
          <DatabaseBackup className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-primary text-lg font-semibold">Download all data</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Export a complete backup of every record — patients, consultations, grooming,
            boarding, invoices, users and clinical lists. You'll get a single ZIP containing
            a <b>JSON file per table</b> (best for restoring) plus a <b>full Excel workbook</b>
            (one sheet per table, easy to read).
          </p>
          <ul className="text-xs text-muted-foreground mt-3 space-y-1">
            <li className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Admin only — passwords are never included.</li>
            <li className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Keep a copy off-site (e.g. Google Drive) and download regularly.</li>
          </ul>
          <Button className="mt-5" onClick={download} disabled={downloading} data-testid="download-backup-btn">
            <Download className="h-4 w-4 mr-2" />{downloading ? "Preparing…" : "Download backup (.zip)"}
          </Button>
        </div>
      </div>
    </Card>
  );
}


function ResetDataPanel() {
  const { logout } = useAuth();
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const REQUIRED = "DELETE ALL DATA";
  const run = async () => {
    if (phrase !== REQUIRED) { toast.error(`Type "${REQUIRED}" exactly to confirm`); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-data", { confirm: phrase });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Data reset — removed ${total} records. Kept ${data.kept_admin}.`);
      setPhrase("");
      setTimeout(() => window.location.assign("/"), 1200);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-6 border-destructive/40 bg-destructive/5 max-w-2xl" data-testid="reset-data-panel">
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-xl bg-destructive/15 flex items-center justify-center shrink-0">
          <AlertTriangle className="h-6 w-6 text-destructive" />
        </div>
        <div className="flex-1">
          <h3 className="font-primary text-lg font-semibold text-destructive">Reset all data</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Permanently deletes <b>all clients, pets, consultations, grooming, boarding, appointments,
            invoices, care records and reminders</b>, and removes <b>every user account except the primary
            admin</b> (<code>admin@barkode.vet</code>). Your clinical lists (Treatments, Medicines,
            Diagnostic Tests, Grooming Pricing) are <b>kept</b>.
          </p>
          <p className="text-xs text-destructive/90 mt-2 font-medium">This cannot be undone. Download a backup first if unsure.</p>
          <div className="mt-4 space-y-2">
            <Label>Type <code className="px-1 py-0.5 rounded bg-muted font-mono">DELETE ALL DATA</code> to confirm</Label>
            <Input data-testid="reset-confirm-input" value={phrase} onChange={(e) => setPhrase(e.target.value)}
                   placeholder="DELETE ALL DATA" autoComplete="off" />
          </div>
          <Button variant="destructive" className="mt-4" data-testid="reset-data-btn"
                  disabled={busy || phrase !== REQUIRED} onClick={run}>
            <Trash2 className="h-4 w-4 mr-2" />{busy ? "Resetting…" : "Reset all data"}
          </Button>
        </div>
      </div>
    </Card>
  );
}


export default function Admin() {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/" replace />;
  return (
    <div className="p-4 md:p-8 max-w-[1300px] mx-auto animate-fade-in">
      <div className="mb-6">
        <h1 className="font-primary text-3xl md:text-4xl font-bold tracking-tight">Admin</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage users, clinical lists and clinic branding.</p>
      </div>

      <Tabs defaultValue="analytics">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="analytics" data-testid="tab-analytics"><BarChart3 className="h-4 w-4 mr-2" />Analytics</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users"><Users className="h-4 w-4 mr-2" />Users</TabsTrigger>
          <TabsTrigger value="treatments"><ListChecks className="h-4 w-4 mr-2" />Treatments</TabsTrigger>
          <TabsTrigger value="medicines"><Pill className="h-4 w-4 mr-2" />Medicines</TabsTrigger>
          <TabsTrigger value="tests"><FlaskConical className="h-4 w-4 mr-2" />Diagnostic Tests</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="tab-pricing"><Scissors className="h-4 w-4 mr-2" />Grooming Pricing</TabsTrigger>
          <TabsTrigger value="backup" data-testid="tab-backup"><DatabaseBackup className="h-4 w-4 mr-2" />Data Backup</TabsTrigger>
          <TabsTrigger value="reset" data-testid="tab-reset" className="text-destructive data-[state=active]:text-destructive"><AlertTriangle className="h-4 w-4 mr-2" />Reset Data</TabsTrigger>
        </TabsList>
        <TabsContent value="analytics"><AnalyticsPanel /></TabsContent>
        <TabsContent value="users"><StaffPanel /></TabsContent>
        <TabsContent value="treatments"><CrudList title="Treatments" endpoint="/treatments" testid="treatment" /></TabsContent>
        <TabsContent value="medicines"><CrudList title="Medicines" endpoint="/medicines" testid="medicine" /></TabsContent>
        <TabsContent value="tests"><CrudList title="Diagnostic Tests" endpoint="/diagnostic-tests" testid="test" /></TabsContent>
        <TabsContent value="pricing"><PricingPanel /></TabsContent>
        <TabsContent value="backup"><BackupPanel /></TabsContent>
        <TabsContent value="reset"><ResetDataPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
